import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import VerifiedShieldIcon from './VerifiedShieldIcon';
import './VerifiedShieldBadge.scss';

type Size = 'sm' | 'md' | 'lg';

interface Props {
  size?: Size;
  className?: string;
  // Extra element treated as "inside" by the click-outside listener.
  // ChatUserAvatar passes its wrapper ref so a wrapper-delegated click
  // doesn't first trigger close-on-mousedown and then re-open from the
  // wrapper's onClick (which caused a visible open/close flicker).
  triggerRef?: React.RefObject<HTMLElement>;
  // Overrides both the tooltip text and the aria-label. Defaults to the
  // "Verified person" copy used by verified-user avatars. Listings pass
  // "Verified seller" / "Verified buyer".
  label?: string;
  // Exact pixel size override, when the sm/md/lg presets don't fit. Takes
  // precedence over `size`.
  sizePx?: number;
}

const SIZE_PX: Record<Size, number> = { sm: 14, md: 18, lg: 36 };

interface TooltipPos {
  top: number;
  left: number;
  arrowOffset: number;
  placement: 'above' | 'below';
}

const VIEWPORT_MARGIN = 8;
const TOOLTIP_GAP = 6;

const VerifiedShieldBadge: React.FC<Props> = ({ size = 'md', className, triggerRef, label, sizePx }) => {
  const { t } = useTranslation();
  const tooltipText = label ?? t('verification.verifiedPerson');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPos | null>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideBadge = ref.current?.contains(target);
      const insideTrigger = triggerRef?.current?.contains(target);
      if (!insideBadge && !insideTrigger) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, triggerRef]);

  // Position tooltip relative to badge in viewport coords, clamping
  // its center into the viewport so it doesn't clip near edges (mobile
  // chat avatars trigger this). Arrow `::after` shifts back toward the
  // badge via --arrow-offset so the pointer stays aligned.
  useLayoutEffect(() => {
    if (!open || !ref.current) {
      setPos(null);
      return;
    }
    const update = () => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const desiredCenter = r.left + r.width / 2;
      const tipWidth = tooltipRef.current?.offsetWidth ?? 0;
      const tipHeight = tooltipRef.current?.offsetHeight ?? 0;
      const halfW = tipWidth / 2;
      const minCenter = halfW + VIEWPORT_MARGIN;
      const maxCenter = window.innerWidth - halfW - VIEWPORT_MARGIN;
      const clampedCenter =
        tipWidth > 0 ? Math.max(minCenter, Math.min(maxCenter, desiredCenter)) : desiredCenter;
      // Prefer below the badge (so it can't collide with the username
      // tooltip rendered above the avatar). Flip above only if there's
      // no room below — e.g. avatar near viewport bottom (latest chat
      // message). Need a measured height; first pass with 0 falls
      // through to "below", second RAF pass re-evaluates with real
      // height.
      const roomBelow = window.innerHeight - r.bottom;
      const placement: 'above' | 'below' =
        tipHeight > 0 && roomBelow < tipHeight + TOOLTIP_GAP + VIEWPORT_MARGIN
          ? 'above'
          : 'below';
      const top = placement === 'below' ? r.bottom : r.top;
      setPos({
        top,
        left: clampedCenter,
        arrowOffset: desiredCenter - clampedCenter,
        placement,
      });
    };
    update();
    // Second pass after the portal mounts so we can measure tooltip width.
    const raf = requestAnimationFrame(update);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const px = sizePx ?? SIZE_PX[size];

  return (
    <button
      ref={ref}
      type="button"
      className={`verified-shield-badge verified-shield-badge--${size}${className ? ' ' + className : ''}`}
      style={{ width: px, height: px }}
      onClick={(e) => {
        e.stopPropagation();
        setOpen((v) => !v);
      }}
      aria-label={tooltipText}
    >
      <VerifiedShieldIcon px={px} />
      {open && pos && createPortal(
        <span
          ref={tooltipRef}
          className="verified-shield-badge__tooltip"
          role="tooltip"
          data-placement={pos.placement}
          style={{
            top: pos.top,
            left: pos.left,
            ['--arrow-offset' as never]: `${pos.arrowOffset}px`,
          }}
        >
          {tooltipText}
        </span>,
        document.body,
      )}
    </button>
  );
};

export default VerifiedShieldBadge;
